import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { makeCollapsible } from '../util/collapsible.js';
import { formatCrowns } from '../util/formatBig.js';
import { cgTable, cgCol, cgRow } from '../util/costReward.js';
import { attachItemInfoToggle } from '../components/ItemInfoPanel.js';
import { flashToast } from '../util/toast.js';
import { showLootChanceDialog } from '../components/LootChanceDialog.js';
import { ITEM_TIER_LABELS } from '@rex-mortis/shared';
import type {
  KingdomDto,
  ShipDto,
  TravelStatusResponse,
} from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

let countdownTimer: number | null = null;
function clearCountdown(): void {
  if (countdownTimer !== null) {
    window.clearInterval(countdownTimer);
    countdownTimer = null;
  }
}

export async function renderTravel(root: HTMLElement): Promise<void> {
  let status: TravelStatusResponse | null = null;
  let ships: ShipDto[] = [];
  let flash: FlashState | null = null;

  async function refreshStatus(): Promise<void> {
    try {
      status = await api.travelStatus();
      store.set({ character: status.character });
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Failed to load travel state.';
      flash = { kind: 'danger', message: msg };
    }
  }

  async function refreshShips(): Promise<void> {
    try {
      ships = (await api.listShips()).ships;
    } catch {
      // tolerated
    }
  }

  await refreshStatus();
  await refreshShips();

  mountShell(root, (main) => {
    const renderMain = () => {
      if (!status) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      const phase = status.phase;
      const trip = status.trip;
      const here = status.currentKingdom;

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/travel.webp" alt="" />
        <h1>The Roads</h1>
        <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
          Foreign roads pay better than your own. They also kill more travellers.
        </p>

        ${phase === 'home' ? `
          <figure style="margin: var(--s-3) 0; border:1px solid var(--c-rule); background: var(--c-parchment); padding: var(--s-2);">
            <img src="/img/kingdoms-map.jpg" alt="Map of the eleven kingdoms"
                 style="width:100%; height:auto; display:block; image-rendering:auto;" />
          </figure>` : ''}

        <div id="flash"></div>

        <div id="phase-panel"></div>

        ${phase === 'home' ? '<div id="kingdom-list"></div><div id="stable-panel"></div>' : ''}
      `;

      const style = document.createElement('style');
      style.textContent = `
        .region-section {
          margin-top: var(--s-5);
        }
        .region-heading {
          font-family: var(--font-display);
          color: var(--c-oxblood);
          border-bottom: 1px solid var(--c-rule);
          padding-bottom: var(--s-1);
          margin: 0 0 var(--s-3) 0;
        }
        .kingdom-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: var(--s-3);
          margin-top: var(--s-2);
        }
        .kingdom-card, .ship-card {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
          display: flex; flex-direction: column;
        }
        /* Per-kingdom artwork strip — a vista of each city on the roads.
           Hidden until the asset exists in /img/kingdoms/.
           The same strip styling is reused for ship portraits (/img/ships/). */
        .kingdom-card .kingdom-art, .ship-card .kingdom-art {
          display: block;
          width: calc(100% + 2 * var(--s-3));
          margin: calc(-1 * var(--s-3)) calc(-1 * var(--s-3)) var(--s-2);
          height: 130px;
          object-fit: cover;
          border-bottom: 1px solid var(--c-rule);
          border-radius: var(--radius) var(--radius) 0 0;
        }
        .kingdom-card h3, .ship-card h3 { margin: 0 0 4px 0; font-size: 1.05rem; }
        .kingdom-card .blurb, .ship-card .blurb {
          font-style: italic; color: var(--c-ink-soft); font-size: 0.92rem;
          margin-bottom: var(--s-2);
        }
        /* Pin the action button to the card's bottom edge so the cost/reward
           table sits directly under the blurb on every card (slack collects
           above the button, not above the table). */
        .kingdom-card .btn, .ship-card .btn { margin-top: auto; }
        .kingdom-card .meta, .ship-card .meta {
          display: flex; flex-wrap: wrap; gap: var(--s-2); margin-bottom: var(--s-2);
          font-size: 0.85rem;
        }
        .kingdom-card .pill, .ship-card .pill {
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule-soft);
          border-radius: 999px;
          padding: 1px 8px;
          font-family: var(--font-numeric);
          color: var(--c-ink-soft);
        }
        .ship-card .pill.active {
          border-color: var(--c-success);
          color: var(--c-success);
        }
        /* Collapsible "Items sold here" inside each visited kingdom card. */
        .kingdom-shop-fold {
          margin-top: var(--s-2);
          border-top: 1px dashed var(--c-rule);
          padding-top: var(--s-2);
        }
        .kingdom-shop-fold summary {
          cursor: pointer;
          font-family: var(--font-heading);
          letter-spacing: 0.04em;
          text-transform: uppercase;
          font-size: 0.82rem;
          color: var(--c-ink-soft);
          padding: 2px 0;
        }
        .kingdom-shop-fold summary:hover { color: var(--c-ink); }
        .kingdom-shop-table {
          width: 100%;
          margin-top: var(--s-2);
          border-collapse: collapse;
          font-size: 0.85rem;
        }
        .kingdom-shop-table th, .kingdom-shop-table td {
          padding: 4px 6px;
          border-bottom: 1px dashed var(--c-rule);
          text-align: left;
          color: var(--c-ink);
        }
        .kingdom-shop-table th { color: var(--c-ink-soft); font-weight: 600; }
        .kingdom-shop-table .num { text-align: right; font-family: var(--font-numeric); }
        .shop-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: var(--s-3);
          margin-top: var(--s-3);
        }
        .shop-card {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
        }
        .shop-card h3 { margin: 0 0 4px 0; font-size: 1.0rem; }
        .shop-card .row { display:flex; justify-content:space-between; gap: var(--s-2); align-items: end; }
        /* Layout only — NEVER set background/color here. The global pinned cream
           cell in pages.css owns form-control colours (dark ink on cream in BOTH
           themes). Re-declaring them with theme tokens flipped to dark-on-dark in
           some foreign-kingdom themes (the black-on-black qty box). Border pinned
           to a dark literal so it reads on the cream cell in either theme. */
        .shop-card .qty {
          width: 50px; padding: 2px 6px; font: inherit;
          border: 1px solid rgba(28, 17, 8, 0.25);
          border-radius: var(--radius);
        }
        .stable-section h2 {
          font-family: var(--font-display);
          color: var(--c-oxblood);
          border-bottom: 1px solid var(--c-rule);
          padding-bottom: var(--s-1);
          margin: var(--s-5) 0 var(--s-3) 0;
        }
        .panel-big {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-4);
          margin-bottom: var(--s-4);
        }
      `;
      main.appendChild(style);

      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        const div = document.createElement('div');
        div.className = `alert ${flash.kind === 'warn' ? 'danger' : flash.kind}`;
        div.textContent = flash.message;
        flashRoot.appendChild(div);
      }

      const phasePanel = main.querySelector<HTMLDivElement>('#phase-panel')!;

      // ── Phase: outbound or inbound ────────────────────────────────────────
      if ((phase === 'outbound' || phase === 'inbound') && trip) {
        const dest = phase === 'outbound' ? trip.toKingdom : 'Rex Mortis';
        phasePanel.innerHTML = `
          <div class="panel-big">
            <h2>${phase === 'outbound' ? `On the road to ${escapeHtml(prettyKingdom(dest))}` : 'Returning home'}</h2>
            <p>From <strong>${escapeHtml(prettyKingdom(trip.fromKingdom))}</strong> to <strong>${escapeHtml(prettyKingdom(trip.toKingdom))}</strong>.</p>
            <p>Arrives in <span id="cd">${formatRemaining(trip.arrivesAt)}</span>.</p>
          </div>
        `;
        clearCountdown();
        countdownTimer = window.setInterval(async () => {
          const cd = main.querySelector<HTMLSpanElement>('#cd');
          if (!cd) {
            clearCountdown();
            return;
          }
          const r = formatRemaining(trip.arrivesAt);
          cd.textContent = r;
          if (r === '0s') {
            clearCountdown();
            await refreshStatus();
            // Arrival re-renders the whole page; land the player at the top
            // rather than restoring a stale mid-page offset.
            anchored(renderMain, { top: true });
          }
        }, 1000);
        return;
      }

      // ── Phase: at a foreign kingdom ───────────────────────────────────────
      if (phase === 'at_kingdom' && here && trip) {
        const remaining = trip.carryCapacity - trip.itemsBought;
        phasePanel.innerHTML = `
          <img class="page-banner" src="/img/kingdoms/${here.slug}.webp" alt="${escapeHtml(here.name)}" onerror="this.remove()" />
          <div class="panel-big">
            <h2>${escapeHtml(here.name)}</h2>
            <p style="font-style: italic; color: var(--c-ink-soft);">${escapeHtml(here.blurb)}</p>
            <dl class="kv">
              <dt>Carry capacity</dt><dd>${trip.itemsBought} / ${trip.carryCapacity} (${remaining} left)</dd>
              <dt>Crowns on hand</dt><dd>${formatCrowns(status.character.crowns)}</dd>
            </dl>
            <button class="btn" id="return-home">Travel home (${formatDuration(here.travelTimeSeconds)})</button>
          </div>
          <div id="fishing-panel"></div>
          <h2 style="font-family:var(--font-display); color:var(--c-oxblood); border-bottom:1px solid var(--c-rule); padding-bottom:var(--s-1); margin: var(--s-5) 0 var(--s-3) 0;">${escapeHtml(here.name)} Shop</h2>
          <div class="shop-grid" id="shop"></div>
        `;
        const returnBtn = phasePanel.querySelector<HTMLButtonElement>('#return-home');
        returnBtn?.addEventListener('click', async () => {
          returnBtn.disabled = true;
          try {
            const r = await api.depart({ toKingdom: 'rex_mortis' });
            store.set({ character: r.character });
            flash = {
              kind: 'success',
              message: `Set out for home. Arriving ${new Date(r.trip.arrivesAt).toLocaleTimeString()}.`,
            };
            await refreshStatus();
          } catch (err) {
            const msg = err instanceof HttpError ? err.message : 'Could not depart.';
            flash = { kind: 'danger', message: msg };
          } finally {
            anchored(renderMain);
          }
        });

        const shop = phasePanel.querySelector<HTMLDivElement>('#shop')!;
        for (const item of here.shop ?? []) {
          const card = document.createElement('div');
          card.className = 'shop-card';
          const tierLabel = ITEM_TIER_LABELS[item.tier as keyof typeof ITEM_TIER_LABELS] ?? item.tier;
          // Buyable now = the lesser of carry space left and the city's shared
          // stock (stock refills toward stockMax each hour, shared by all players).
          const buyable = Math.max(0, Math.min(remaining, item.stock));
          const soldOut = item.stock < 1;
          card.innerHTML = `
            <h3><span class="shop-item-name" data-slug="${escapeHtml(item.itemSlug)}">${escapeHtml(item.name)}</span></h3>
            <p style="font-style:italic;color:var(--c-ink-soft);font-size:0.9rem;margin:4px 0;">${escapeHtml(item.blurb)}</p>
            ${cgTable([
              cgCol('Cost', [cgRow('Price', `${formatCrowns(item.price)} ea`, 'gain')]),
              cgCol('Goods', [
                cgRow('Tier', tierLabel),
                cgRow('In stock', `${item.stock} / ${item.stockMax}`, soldOut ? 'warn' : ''),
              ]),
            ])}
            <div class="row">
              <input class="qty" type="number" min="1" max="${buyable}" value="1" data-buy-qty="${item.itemSlug}" ${buyable < 1 ? 'disabled' : ''} />
              <button class="btn" data-buy="${item.itemSlug}" ${buyable < 1 ? 'disabled' : ''}>
                ${remaining < 1 ? 'Carry full' : soldOut ? 'Sold out' : 'Buy'}
              </button>
            </div>
          `;
          shop.appendChild(card);
        }
        // Clickable item names — open the shared item-detail modal, like the
        // Inventory and Market, so shoppers can inspect a foreign item's stats.
        shop.querySelectorAll<HTMLElement>('.shop-item-name[data-slug]').forEach((el) => {
          attachItemInfoToggle(el, el.dataset.slug!);
        });
        shop.querySelectorAll<HTMLButtonElement>('button[data-buy]').forEach((btn) => {
          btn.addEventListener('click', async () => {
            const slug = btn.dataset.buy!;
            const qInput = shop.querySelector<HTMLInputElement>(`input[data-buy-qty="${slug}"]`);
            const qty = Math.max(1, Math.floor(Number(qInput?.value ?? '1')));
            shop.querySelectorAll<HTMLButtonElement>('button[data-buy]').forEach((b) => (b.disabled = true));
            try {
              const r = await api.buyShop({ itemSlug: slug, qty });
              store.set({ character: r.character });
              flash = {
                kind: 'success',
                message: `Bought ${r.qty}× ${r.itemName} for ${formatCrowns(r.totalSpent)} crowns. Carry: ${r.itemsBought}/${r.carryCapacity}. Stock left: ${r.stockRemaining}.`,
              };
              await refreshStatus();
            } catch (err) {
              const msg = err instanceof HttpError ? err.message : 'Buy failed.';
              flash = { kind: 'danger', message: msg };
            } finally {
              anchored(renderMain);
            }
          });
        });

        // ── Gathering activities available at this city (fishing/hunting/…) ───
        void (async () => {
          const host = phasePanel.querySelector<HTMLDivElement>('#fishing-panel');
          if (!host) return;
          let gs;
          try {
            gs = await api.getGatheringState();
          } catch {
            return;
          }
          if (!gs.activities.length) return;
          host.innerHTML = gs.activities
            .map((a, i) => {
              const f = a.skill;
              const pct = f.xpForNextLevel ? Math.min(100, (f.xpIntoLevel / f.xpForNextLevel) * 100) : 100;
              const maxAct = Math.min(
                a.maxVigorPerAction,
                Math.floor(gs!.vigorNow / a.vigorPerAttempt) * a.vigorPerAttempt,
              );
              const canAct = maxAct >= a.vigorPerAttempt;
              const toolLine = a.tool
                ? `${escapeHtml(a.tool.name)} <span style="color:var(--c-ink-soft);">(power ${a.tool.power})</span>`
                : `<span style="color:var(--c-ink-soft);">Bare-handed — equip a ${escapeHtml(a.toolNoun)} for better odds</span>`;
              const verbPlural = `${a.actionVerb.toLowerCase()}s`;
              const startVigor = canAct ? maxAct : a.vigorPerAttempt;
              const startActs = Math.floor(startVigor / a.vigorPerAttempt);
              return `
                <div class="panel-big" style="margin-top: var(--s-4);">
                  <img class="page-banner" src="/img/banners/trade-${escapeHtml(a.skillKey)}.webp" alt="" onerror="this.style.display='none'" style="margin: -4px 0 var(--s-3);" />
                  <div style="font-size:0.78rem; letter-spacing:0.12em; text-transform:uppercase; color:var(--c-gold-text); font-weight:700;">${escapeHtml(a.skillName)}</div>
                  <h2 style="font-family:var(--font-display); color:var(--c-oxblood); margin-top:2px;">${escapeHtml(sentenceCase(a.spotName))}</h2>
                  <p style="font-style:italic; color: var(--c-ink-soft);">${escapeHtml(a.spotBlurb)}</p>
                  <dl class="kv">
                    <dt>${escapeHtml(a.skillName)}</dt><dd>Level ${f.level}${f.xpForNextLevel ? ` · ${f.xpIntoLevel.toLocaleString()} / ${f.xpForNextLevel.toLocaleString()} XP` : ' · mastered'}</dd>
                    <dt>Tool</dt><dd>${toolLine}</dd>
                  </dl>
                  <div style="height:8px; background:rgba(28,17,8,0.18); border:1px solid var(--c-rule); border-radius:2px; overflow:hidden; max-width:420px; margin: 2px 0 12px;">
                    <span style="display:block; height:100%; width:${pct.toFixed(1)}%; background:linear-gradient(to bottom, var(--c-gold-bright), var(--c-gold));"></span>
                  </div>
                  <div class="row" style="align-items:center; gap:var(--s-2); flex-wrap:wrap;">
                    <input class="qty" type="number" id="gather-vigor-${i}" min="${a.vigorPerAttempt}" max="${canAct ? maxAct : a.vigorPerAttempt}" step="${a.vigorPerAttempt}" value="${startVigor}" ${canAct ? '' : 'disabled'} style="width:90px;" />
                    <span id="gather-acts-${i}" style="color:var(--c-gold-text); font-weight:700; font-size:0.9rem; min-width:90px;">= ${startActs} ${escapeHtml(verbPlural)}</span>
                    <button class="btn gold" data-gather="${escapeHtml(a.skillKey)}" data-vinput="gather-vigor-${i}" data-vmin="${a.vigorPerAttempt}" data-vstep="${a.vigorPerAttempt}" data-acts="gather-acts-${i}" data-verb="${escapeHtml(verbPlural)}" ${canAct ? '' : 'disabled'}>${canAct ? `${escapeHtml(a.actionVerb)}` : 'Need more vigor'}</button>
                    <span style="color:var(--c-ink-soft); font-size:0.85rem;">${a.vigorPerAttempt} vigor per ${escapeHtml(a.actionVerb.toLowerCase())}</span>
                  </div>
                  <div style="margin-top:12px;">
                    <button class="btn" data-gather-loot="${i}" style="font-size:0.9rem; padding:5px 12px;">What you might ${escapeHtml(a.takeVerb)} ⓘ</button>
                  </div>
                </div>`;
            })
            .join('');
          // "What you might take" → pop-out odds table (like the Deeds loot popup).
          host.querySelectorAll<HTMLButtonElement>('button[data-gather-loot]').forEach((btn) => {
            btn.addEventListener('click', () => {
              const a = gs!.activities[Number(btn.dataset.gatherLoot)];
              if (!a) return;
              const lockedFinds = a.catchable.filter((c) => c.locked);
              const lockedNote = lockedFinds.length
                ? `Unlocks as your skill rises: ${lockedFinds.map((c) => `${c.name} (Lvl ${c.minLevel})`).join(', ')}.`
                : '';
              showLootChanceDialog({
                title: `${a.skillName} — ${sentenceCase(a.spotName)}`,
                dropLabel: `What you might ${a.takeVerb} (per attempt)`,
                dropChance: 1,
                dropTable: a.catchable
                  .filter((c) => !c.locked)
                  .map((c) => ({ itemSlug: c.slug, itemName: c.name, weight: c.weight, qtyMin: c.qtyMin, qtyMax: c.qtyMax })),
                ...(lockedNote ? { note: lockedNote } : {}),
                footnote: 'Every attempt lands one find, picked by its weight share below. A better tool shifts the odds toward the rarer finds; raising your skill unlocks the harder ones.',
              });
            });
          });
          // Live "= N actions" readout as the vigor input changes.
          host.querySelectorAll<HTMLButtonElement>('button[data-gather]').forEach((btn) => {
            const vin = host.querySelector<HTMLInputElement>(`#${btn.dataset.vinput}`);
            const acts = host.querySelector<HTMLSpanElement>(`#${btn.dataset.acts}`);
            const step = Number(btn.dataset.vstep ?? '1');
            const verb = btn.dataset.verb ?? 'actions';
            if (vin && acts) {
              const update = (): void => {
                const n = Math.max(0, Math.floor(Number(vin.value || 0) / step));
                acts.textContent = `= ${n} ${verb}`;
              };
              vin.addEventListener('input', update);
            }
          });
          host.querySelectorAll<HTMLButtonElement>('button[data-gather]').forEach((btn) => {
            btn.addEventListener('click', async () => {
              const skill = btn.dataset.gather!;
              const vmin = Number(btn.dataset.vmin ?? '1');
              const vin = host.querySelector<HTMLInputElement>(`#${btn.dataset.vinput}`);
              const vigor = Math.max(vmin, Math.floor(Number(vin?.value ?? vmin)));
              host.querySelectorAll<HTMLButtonElement>('button[data-gather]').forEach((b) => (b.disabled = true));
              try {
                const r = await api.gather(skill, vigor);
                store.set({ character: r.character });
                const caught = r.catches.length
                  ? r.catches.map((cc) => `${cc.qty}× ${cc.name}`).join(', ')
                  : 'nothing of worth';
                const lvlUp = r.skillLevelAfter > r.skillLevelBefore ? ` Your ${r.skillName} rose to Level ${r.skillLevelAfter}!` : '';
                // Floating notification (like Deeds), not an in-page banner.
                flashToast({
                  kind: 'success',
                  message: `${r.attempts}× at ${r.spotName}. Took: ${caught}. +${r.skillXpGained} ${r.skillName.toLowerCase()} XP.${lvlUp}`,
                });
                await refreshStatus();
              } catch (err) {
                flashToast({ kind: 'danger', message: err instanceof HttpError ? err.message : 'No luck this time.' });
              } finally {
                anchored(renderMain);
              }
            });
          });
        })();
        return;
      }

      // ── Phase: home — list of kingdoms grouped by region + stable ─────────
      // Count distinct regions for the welcome line.
      const regionCount = new Set(status.kingdoms.map((k) => k.region)).size;
      phasePanel.innerHTML = `
        <div class="panel-big">
          <h2>You are in Rex Mortis</h2>
          <p>${regionCount} foreign realms lie beyond the city gates, each with several destinations. Every place has goods you cannot buy at home.</p>
          <p>Active ship: <strong>${formatActiveShip(status.character.shipSlug)}</strong>. Faster ships shorten every crossing.</p>
        </div>
      `;
      const kl = main.querySelector<HTMLDivElement>('#kingdom-list')!;

      // Group destinations by region, preserving the catalog's order within each.
      const byRegion = new Map<string, { regionName: string; dests: typeof status.kingdoms }>();
      for (const k of status.kingdoms) {
        let g = byRegion.get(k.region);
        if (!g) {
          g = { regionName: k.regionName, dests: [] as unknown as typeof status.kingdoms };
          byRegion.set(k.region, g);
        }
        (g.dests as typeof status.kingdoms).push(k);
      }

      for (const [region, group] of byRegion) {
        const section = document.createElement('section');
        section.className = 'region-section';
        section.innerHTML = `
          <h3 class="region-heading">${escapeHtml(group.regionName)}</h3>
        `;
        const grid = document.createElement('div');
        grid.className = 'kingdom-grid';
        for (const k of group.dests) {
          const card = document.createElement('div');
          card.className = 'kingdom-card';
          const visits = k.visitCount ?? 0;
          const visitPill = visits > 0
            ? `<span class="pill" style="background: var(--c-gold-soft, rgba(212,169,60,0.18));">Visited ${visits}×</span>`
            : '';
          // The trades plied at this city, with their unique named spots —
          // revealed once the traveller has been there. Server populates
          // `trades` only when visitCount > 0.
          const tradesSection = visits > 0 && k.trades && k.trades.length > 0
            ? `
              <details class="kingdom-shop-fold">
                <summary>Trades plied here (${k.trades.length})</summary>
                <table class="kingdom-shop-table">
                  <tbody>
                    ${k.trades
                      .map(
                        (t) => `
                          <tr>
                            <td style="white-space:nowrap;">${escapeHtml(t.skill)}</td>
                            <td style="font-style:italic; color:var(--c-ink-soft);">${escapeHtml(sentenceCase(t.spotName))}</td>
                          </tr>
                        `,
                      )
                      .join('')}
                  </tbody>
                </table>
              </details>
            `
            : '';
          // Show the kingdom's shop only when the player has been there before.
          // Server only populates `shop` on the home-view kingdom list once
          // visitCount > 0, so the presence of `shop` is itself the gate.
          const shopSection = visits > 0 && k.shop && k.shop.length > 0
            ? `
              <details class="kingdom-shop-fold">
                <summary>Items sold here (${k.shop.length})</summary>
                <table class="kingdom-shop-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Tier</th>
                      <th class="num">Price</th>
                      <th class="num">Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${k.shop
                      .map(
                        (s) => `
                          <tr>
                            <td>${escapeHtml(s.name)}</td>
                            <td class="tier-${s.tier}">${s.tier}</td>
                            <td class="num">${s.price.toLocaleString()}</td>
                            <td class="num">${s.stock}/${s.stockMax}</td>
                          </tr>
                        `,
                      )
                      .join('')}
                  </tbody>
                </table>
              </details>
            `
            : '';
          card.innerHTML = `
            <img class="kingdom-art" src="/img/kingdoms/${k.slug}.webp" alt="" onerror="this.remove()" />
            <h3>${escapeHtml(k.name)}</h3>
            <div class="blurb">${escapeHtml(k.blurb)}</div>
            <div class="meta">
              <span class="pill">${formatDuration(k.travelTimeSeconds)} each way</span>
              ${visitPill}
            </div>
            <button class="btn" data-go="${k.slug}">Travel here</button>
            ${tradesSection}
            ${shopSection}
          `;
          grid.appendChild(card);
        }
        section.appendChild(grid);
        kl.appendChild(section);
        // Fold/unfold each region's city list; state persists per region.
        const headingEl = section.querySelector<HTMLElement>('.region-heading');
        if (headingEl) makeCollapsible(headingEl, grid, `travel:region:${region}`);
      }
      kl.querySelectorAll<HTMLButtonElement>('button[data-go]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.go!;
          kl.querySelectorAll<HTMLButtonElement>('button[data-go]').forEach((b) => (b.disabled = true));
          try {
            const r = await api.depart({ toKingdom: slug });
            store.set({ character: r.character });
            flash = {
              kind: 'success',
              message: `Set out for ${prettyKingdom(slug)}. Arriving ${new Date(r.trip.arrivesAt).toLocaleTimeString()}.`,
            };
            await refreshStatus();
          } catch (err) {
            const msg = err instanceof HttpError ? err.message : 'Could not depart.';
            flash = { kind: 'danger', message: msg };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // Shipyard panel
      const sp = main.querySelector<HTMLDivElement>('#stable-panel')!;
      sp.innerHTML = `
        <div class="stable-section">
          <h2>Royal Shipyard</h2>
        </div>
      `;
      // Collapsible content for the shipyard: intro line + ship grid.
      const shipyardContent = document.createElement('div');
      shipyardContent.innerHTML = `
        <p style="font-style:italic;color:var(--c-ink-soft)">
          Every ship in your fleet stays yours. Switch between them at no cost — each has its own perks.
        </p>
      `;
      const sGrid = document.createElement('div');
      sGrid.className = 'kingdom-grid';
      for (const s of ships) {
        const card = document.createElement('div');
        card.className = 'ship-card';

        // Reward rows: speed plus any non-zero perk bonuses.
        const shipRewardRows: string[] = [cgRow('Speed', `×${s.speedMultiplier}`, 'gain')];
        if (s.carryBonus > 0) shipRewardRows.push(cgRow('Cargo', `+${s.carryBonus}`, 'gain'));
        if (s.crimeBoostPercent > 0) shipRewardRows.push(cgRow('Cunning', `+${s.crimeBoostPercent}%`, 'gain'));
        if (s.marketResaleBoostPercent > 0) shipRewardRows.push(cgRow('Resale', `+${s.marketResaleBoostPercent}%`, 'gain'));
        const shipCostRow = s.costCrowns > 0 ? cgRow('Crowns', formatCrowns(s.costCrowns)) : cgRow('Price', 'Free', 'gain');

        // Choose button state based on owned/active.
        let buttonHtml = '';
        if (s.active) {
          buttonHtml = `<button class="btn" disabled>Active</button>`;
        } else if (s.owned) {
          buttonHtml = `<button class="btn" data-switch="${s.slug}">Switch to this ship</button>`;
        } else {
          buttonHtml = `<button class="btn" data-ship="${s.slug}">Buy for ${formatCrowns(s.costCrowns)} crowns</button>`;
        }

        card.innerHTML = `
          <img class="kingdom-art" src="/img/ships/${s.slug}.webp" alt="" onerror="this.remove()" />
          <h3>${escapeHtml(s.name)}</h3>
          <div class="blurb">${escapeHtml(s.blurb)}</div>
          ${cgTable([
            cgCol('Cost', [shipCostRow]),
            cgCol('Reward', shipRewardRows),
          ])}
          <div style="font-size:0.88rem; color:var(--c-ink-soft); margin-bottom:var(--s-2);">
            ${escapeHtml(s.perkBlurb)}
          </div>
          ${buttonHtml}
        `;
        sGrid.appendChild(card);
      }
      shipyardContent.appendChild(sGrid);
      sp.appendChild(shipyardContent);
      // Fold/unfold the whole shipyard section; state persists.
      const shipyardHeading = sp.querySelector<HTMLElement>('.stable-section h2');
      if (shipyardHeading) makeCollapsible(shipyardHeading, shipyardContent, 'travel:shipyard');
      sp.querySelectorAll<HTMLButtonElement>('button[data-ship]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.ship!;
          sp.querySelectorAll<HTMLButtonElement>('button[data-ship], button[data-switch]').forEach((b) => (b.disabled = true));
          try {
            const r = await api.buyShip({ shipSlug: slug });
            store.set({ character: r.character });
            flash = {
              kind: 'success',
              message: `Bought the ${r.shipName} for ${formatCrowns(r.costPaid)} crowns. Travel times improved.`,
            };
            await refreshShips();
            await refreshStatus();
          } catch (err) {
            const msg = err instanceof HttpError ? err.message : 'Shipyard purchase failed.';
            flash = { kind: 'danger', message: msg };
          } finally {
            anchored(renderMain);
          }
        });
      });
      sp.querySelectorAll<HTMLButtonElement>('button[data-switch]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.switch!;
          sp.querySelectorAll<HTMLButtonElement>('button[data-ship], button[data-switch]').forEach((b) => (b.disabled = true));
          try {
            const r = await api.switchShip({ shipSlug: slug });
            store.set({ character: r.character });
            flash = {
              kind: 'success',
              message: `Put to sea in the ${r.shipName}. The crossings have changed character.`,
            };
            await refreshShips();
            await refreshStatus();
          } catch (err) {
            const msg = err instanceof HttpError ? err.message : 'Could not switch ship.';
            flash = { kind: 'danger', message: msg };
          } finally {
            anchored(renderMain);
          }
        });
      });
    };

    anchored(renderMain);
  });

  window.addEventListener('hashchange', clearCountdown, { once: true });
}

function formatActiveShip(slug: string | null): string {
  if (!slug || slug === 'coracle') return 'Coracle';
  return prettyShipName(slug);
}

function prettyShipName(slug: string): string {
  const map: Record<string, string> = {
    coracle: 'Coracle',
    cog: 'Cog',
    caravel: 'Caravel',
    sloop: 'Sloop',
    carrack: 'Carrack',
    galleon: 'Galleon',
    leviathan: 'Leviathan',
  };
  return map[slug] ?? slug;
}

function prettyKingdom(slug: string): string {
  if (slug === 'rex_mortis') return 'Rex Mortis';
  return slug
    .split('_')
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join(' ');
}

function formatRemaining(iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return '0s';
  const sec = Math.ceil(ms / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

/** Capitalize the first letter so a phrase reads as a proper sentence start. */
function sentenceCase(s: string): string {
  return s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
